import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

const FinancialChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const didSubmitRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  // Re-focus the input after a request finishes (so the user can type the next prompt immediately).
  useEffect(() => {
    if (!isLoading && didSubmitRef.current) {
      didSubmitRef.current = false;
      // Defer to next frame so the input is re-enabled before focusing.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isLoading]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const startTime = Date.now(); // Track start time
    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    didSubmitRef.current = true;
    
    // Add placeholder for assistant message immediately for better UX
    setMessages(prev => [...prev, { role: 'assistant', content: '', startTime }]);

    try {
      const response = await fetch('/api/v1/chat/financial-query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          useCache: true
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body.getReader();
      let assistantMessage = '';

      // Read the streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('0:')) {
            const data = line.slice(2);
            // Unescape newlines that were escaped for streaming
            const unescapedData = data.replace(/\\n/g, '\n');
            assistantMessage += unescapedData;
            
            // Update the last message (assistant's response) in real-time
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              newMessages[newMessages.length - 1] = {
                role: 'assistant',
                content: assistantMessage,
                startTime: lastMessage.startTime // Preserve start time
              };
              return newMessages;
            });
          }
        }
      }
      
      // Add completion time to the final message
      const endTime = Date.now();
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        const responseTime = ((endTime - lastMessage.startTime) / 1000).toFixed(1);
        newMessages[newMessages.length - 1] = {
          ...lastMessage,
          responseTime: responseTime
        };
        return newMessages;
      });
      
    } catch (error) {
      console.error('Chat error:', error);
      // Update the last message with error instead of adding a new one
      const endTime = Date.now();
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        const responseTime = lastMessage.startTime ? ((endTime - lastMessage.startTime) / 1000).toFixed(1) : '0.0';
        newMessages[newMessages.length - 1] = {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          responseTime: responseTime
        };
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full bg-white relative">
      <div className="max-w-4xl mx-auto pt-4 h-full flex flex-col">
        <div className="flex-shrink-0 p-6  pb-4">
          <h1 className=" text-2xl px-4 font-semibold text-gray-900">Finance Chat</h1>
          <p className="mt-1 px-4 text-sm text-gray-600">
            Ask questions about your Profit & Loss data
          </p>
        </div>
        
        <div className="flex-1 overflow-auto px-6 pb-32">
          <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`px-4 py-3 rounded-lg ${
                      message.role === 'user'
                        ? 'max-w-xs lg:max-w-md bg-gray-200 text-gray-900'
                        : 'max-w-md lg:max-w-2xl text-gray-900'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <div className="text-base whitespace-pre-wrap">
                        {message.content}
                      </div>
                    ) : (
                      <div className="text-base prose prose-lg prose-slate max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-em:text-gray-700 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-li:text-gray-700 prose-li:marker:text-blue-600">
                        <ReactMarkdown 
                          components={{
                            h1: ({node, ...props}) => <h1 className="text-xl font-bold text-gray-900 mb-3 mt-4 first:mt-0" {...props} />,
                            h2: ({node, ...props}) => <h2 className="text-lg font-bold text-gray-900 mb-2 mt-4 first:mt-0" {...props} />,
                            h3: ({node, ...props}) => <h3 className="text-base font-semibold text-gray-900 mb-2 mt-3 first:mt-0" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc ml-4 space-y-1 mb-3 text-gray-700" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal ml-4 space-y-1 mb-3 text-gray-700" {...props} />,
                            li: ({node, ...props}) => <li className="text-gray-700 leading-relaxed" {...props} />,
                            p: ({node, ...props}) => <p className="mb-3 text-gray-700 leading-relaxed last:mb-0" {...props} />,
                            strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                            em: ({node, ...props}) => <em className="italic text-gray-700" {...props} />,
                            code: ({node, ...props}) => <code className="bg-gray-100 text-gray-900 px-1 py-0.5 rounded text-sm font-mono" {...props} />,
                            blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-blue-500 pl-4 my-3 text-gray-600 italic" {...props} />
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                        
                        {message.responseTime && (
                          <div className="mt-3">
                            <span className="inline-block px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded-full">
                              {message.responseTime}s
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="text-gray-900 max-w-xs lg:max-w-md px-4 py-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                    <span className="text-sm text-gray-500">Assistant is typing...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        <div className="sticky bottom-0 z-50 mt-auto bg-gradient-to-t from-white via-white to-transparent">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <form onSubmit={handleSubmit} className="flex items-center space-x-3">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your P&L data..."
                disabled={isLoading}
                className="flex-1 px-6 h-[52px] text-lg border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="w-[52px] h-[52px] bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialChat;